// PerfectCorp YouCam API Service
// Handles skin analysis using the YCE API v2
// Uses Vite proxy in development, Lambda API Gateway in production

import JSZip from 'jszip'
import { SkinAnalysisError, AnalysisErrorType, isOnline, createOfflineError } from './errors'
import { logError } from './errorLogger'
import { debugLog } from '@/lib/logger'

// In development: Vite proxy handles auth and forwards to PerfectCorp
// In production: Lambda API Gateway proxies to PerfectCorp
const isDev = import.meta.env.DEV
const API_BASE = isDev
  ? '/api/perfectcorp'  // Vite proxy rewrites to /s2s/v2.0
  : 'https://fw5uw41stj.execute-api.us-east-1.amazonaws.com/api/perfectcorp'  // Lambda proxy rewrites to /s2s/v2.0

// All 14 HD metrics for full analysis (paid flow)
const HD_ACTIONS_FULL = [
  'hd_wrinkle',
  'hd_pore',
  'hd_texture',
  'hd_acne',
  'hd_oiliness',
  'hd_radiance',
  'hd_eye_bag',
  'hd_age_spot',
  'hd_dark_circle',
  'hd_droopy_upper_eyelid',
  'hd_droopy_lower_eyelid',
  'hd_firmness',
  'hd_moisture',
  'hd_redness',
]

// SD metrics for teaser (free flow) - 4 core metrics
// SD actions don't have hd_ prefix
const SD_ACTIONS_TEASER = [
  'texture',   // Most visually impactful, universally applicable
  'pore',      // Common concern
  'wrinkle',   // Important for skin age
  'redness',   // Visible skin issue
]

// Types for API responses
interface FileUploadResponse {
  status: number
  data: {
    files: Array<{
      file_id: string
      requests: Array<{
        url: string
        headers: Record<string, string>
      }>
    }>
  }
}

interface TaskResponse {
  status: number
  data: {
    task_id?: string
    task_status?: 'running' | 'success' | 'error'
    error?: string | null
    error_message?: string
    results?: {
      url: string
    } | null
  }
}

// Raw score info from the ZIP file (matching actual API response)
// Supports both SD (no prefix) and HD (hd_ prefix) formats
interface RawScoreInfo {
  // HD Metrics with zones (nested structure)
  hd_wrinkle?: {
    forehead?: { ui_score: number; output_mask_name: string }
    glabellar?: { ui_score: number; output_mask_name: string }
    crowfeet?: { ui_score: number; output_mask_name: string }
    periocular?: { ui_score: number; output_mask_name: string }
    nasolabial?: { ui_score: number; output_mask_name: string }
    marionette?: { ui_score: number; output_mask_name: string }
    whole?: { ui_score: number; output_mask_name: string }
  }
  hd_pore?: {
    forehead?: { ui_score: number; output_mask_name: string }
    nose?: { ui_score: number; output_mask_name: string }
    cheek?: { ui_score: number; output_mask_name: string }
    whole?: { ui_score: number; output_mask_name: string }
  }
  hd_texture?: { whole?: { ui_score: number; output_mask_name: string } }
  hd_acne?: { whole?: { ui_score: number; output_mask_name: string } }

  // HD Simple metrics (flat structure)
  hd_oiliness?: { ui_score: number; output_mask_name: string }
  hd_radiance?: { ui_score: number; output_mask_name: string }
  hd_eye_bag?: { ui_score: number; output_mask_name: string }
  hd_age_spot?: { ui_score: number; output_mask_name: string }
  hd_dark_circle?: { ui_score: number; output_mask_name: string }
  hd_droopy_upper_eyelid?: { ui_score: number; output_mask_name: string }
  hd_droopy_lower_eyelid?: { ui_score: number; output_mask_name: string }
  hd_firmness?: { ui_score: number; output_mask_name: string }
  hd_moisture?: { ui_score: number; output_mask_name: string }
  hd_redness?: { ui_score: number; output_mask_name: string }

  // SD Metrics (no hd_ prefix) - simpler structure
  wrinkle?: { whole?: { ui_score: number; output_mask_name: string } }
  pore?: { whole?: { ui_score: number; output_mask_name: string } }
  texture?: { whole?: { ui_score: number; output_mask_name: string } }
  redness?: { ui_score: number; output_mask_name: string }

  // Overall results
  all?: { score: number }
  skin_age?: number
}

// Normalized scores for our app (all metrics flattened)
export interface SkinAnalysisScores {
  // Main metrics (ui_score from API, higher = better)
  wrinkles: number
  pores: number
  texture: number
  acne: number
  oiliness: number
  radiance: number
  eyeBags: number
  spots: number
  darkCircles: number
  droopyEyelidUpper: number
  droopyEyelidLower: number
  firmness: number
  moisture: number
  redness: number

  // Zone-specific scores
  wrinkleZones?: {
    forehead: number
    glabellar: number
    crowfeet: number
    periocular: number
    nasolabial: number
    marionette: number
  }
  poreZones?: {
    forehead: number
    nose: number
    cheek: number
  }
}

// Zone-specific heatmaps (HD only)
export interface ZoneHeatmaps {
  wrinkleZones?: {
    forehead?: string
    glabellar?: string
    crowfeet?: string
    periocular?: string
    nasolabial?: string
    marionette?: string
    whole?: string
  }
  poreZones?: {
    forehead?: string
    nose?: string
    cheek?: string
    whole?: string
  }
}

export interface SkinAnalysisResult {
  skinAge: number
  skinScore: number
  scores: SkinAnalysisScores
  heatmaps: Record<string, string> // metric name -> base64 image data URL
  zoneHeatmaps?: ZoneHeatmaps // Structured zone heatmaps for UI
}

/**
 * Upload a file to PerfectCorp and get a file_id
 * Uses proxy which adds Authorization header
 */
async function uploadFile(file: File): Promise<string> {
  // Check online status before attempting upload
  if (!isOnline()) {
    throw createOfflineError()
  }

  // Step 1: Get presigned upload URL (via proxy)
  debugLog('[PerfectCorp] uploadFile: Requesting presigned URL...')
  debugLog('[PerfectCorp] uploadFile: File info -', file.name, file.size, file.type)
  debugLog('[PerfectCorp] uploadFile: API_BASE =', API_BASE)

  let response: Response
  try {
    response = await fetch(`${API_BASE}/file/skin-analysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        files: [{
          content_type: file.type,
          file_name: file.name,
          file_size: file.size,
        }],
      }),
    })
    debugLog('[PerfectCorp] uploadFile: Response status =', response.status)
  } catch (fetchError) {
    console.error('[PerfectCorp] uploadFile: Fetch failed!', fetchError)
    // Network/fetch errors
    const errorMsg = fetchError instanceof Error ? fetchError.message : String(fetchError)
    throw new SkinAnalysisError(AnalysisErrorType.NETWORK_ERROR, `Fetch failed: ${errorMsg}`)
  }

  if (!response.ok) {
    const errorText = await response.text()
    console.error('[PerfectCorp] uploadFile: Response not OK:', response.status, errorText)

    // Classify based on HTTP status
    if (response.status >= 500) {
      throw new SkinAnalysisError(AnalysisErrorType.SERVER_ERROR, `Upload URL failed: ${response.status} - ${errorText}`)
    }
    throw new SkinAnalysisError(AnalysisErrorType.UPLOAD_FAILED, `Upload URL failed: ${response.status} - ${errorText}`)
  }

  const data = await response.json() as FileUploadResponse

  if (!data.data?.files?.[0]) {
    throw new SkinAnalysisError(AnalysisErrorType.UPLOAD_FAILED, 'Invalid upload response - no files data')
  }

  const fileData = data.data.files[0]
  const fileId = fileData.file_id
  const uploadUrl = fileData.requests[0].url
  const uploadHeaders = fileData.requests[0].headers

  // Step 2: Upload the actual file to S3 presigned URL (direct, no proxy needed)
  try {
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: uploadHeaders,
      body: file,
    })

    if (!uploadResponse.ok) {
      throw new SkinAnalysisError(AnalysisErrorType.UPLOAD_FAILED, `S3 upload failed: ${uploadResponse.status}`)
    }
  } catch (uploadError) {
    if (uploadError instanceof SkinAnalysisError) throw uploadError
    const errorMsg = uploadError instanceof Error ? uploadError.message : String(uploadError)
    throw new SkinAnalysisError(AnalysisErrorType.UPLOAD_FAILED, `S3 upload error: ${errorMsg}`)
  }

  return fileId
}

/**
 * Start a skin analysis task
 * Uses proxy which adds Authorization header
 * @param fileId - The uploaded file ID
 * @param mode - 'teaser' for SD analysis (4 metrics), 'full' for HD analysis (14 metrics)
 */
async function startAnalysisTask(fileId: string, mode: 'teaser' | 'full' = 'teaser'): Promise<string> {
  // Check online status
  if (!isOnline()) {
    throw createOfflineError()
  }

  // Teaser uses SD (standard) actions, Full uses HD (high-definition) actions
  // Cannot mix SD and HD in the same request per API specification
  const actions = mode === 'full' ? HD_ACTIONS_FULL : SD_ACTIONS_TEASER
  const resolution = mode === 'full' ? 'HD' : 'SD'

  debugLog(`[PerfectCorp] Starting ${mode} analysis (${resolution}) with ${actions.length} metrics:`, actions)

  let response: Response
  try {
    response = await fetch(`${API_BASE}/task/skin-analysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        src_file_id: fileId,
        dst_actions: actions,
      }),
    })
  } catch (fetchError) {
    const errorMsg = fetchError instanceof Error ? fetchError.message : String(fetchError)
    throw new SkinAnalysisError(AnalysisErrorType.NETWORK_ERROR, `Task start fetch failed: ${errorMsg}`)
  }

  if (!response.ok) {
    const errorText = await response.text()
    console.error('[PerfectCorp] startAnalysisTask: Response not OK:', response.status, errorText)

    if (response.status >= 500) {
      throw new SkinAnalysisError(AnalysisErrorType.SERVER_ERROR, `Task start failed: ${response.status} - ${errorText}`)
    }
    throw new SkinAnalysisError(AnalysisErrorType.SERVER_ERROR, `Task start failed: ${response.status} - ${errorText}`)
  }

  const data = await response.json() as TaskResponse

  if (!data.data?.task_id) {
    throw new SkinAnalysisError(AnalysisErrorType.SERVER_ERROR, 'Invalid task response - no task_id')
  }

  return data.data.task_id
}

/**
 * Poll for task completion
 * Uses proxy which adds Authorization header
 */
async function pollTaskStatus(taskId: string, maxAttempts = 60): Promise<string> {
  for (let i = 0; i < maxAttempts; i++) {
    // Check online status during polling
    if (!isOnline()) {
      throw createOfflineError()
    }

    let response: Response
    try {
      response = await fetch(
        `${API_BASE}/task/skin-analysis/${encodeURIComponent(taskId)}`,
        {
          method: 'GET',
        }
      )
    } catch (fetchError) {
      const errorMsg = fetchError instanceof Error ? fetchError.message : String(fetchError)
      throw new SkinAnalysisError(AnalysisErrorType.NETWORK_ERROR, `Poll fetch failed: ${errorMsg}`)
    }

    if (!response.ok) {
      if (response.status >= 500) {
        throw new SkinAnalysisError(AnalysisErrorType.SERVER_ERROR, `Task status check failed: ${response.status}`)
      }
      throw new SkinAnalysisError(AnalysisErrorType.SERVER_ERROR, `Task status check failed: ${response.status}`)
    }

    const data = await response.json() as TaskResponse

    if (data.data?.task_status === 'success' && data.data.results?.url) {
      return data.data.results.url
    }

    if (data.data?.task_status === 'error') {
      const errorMsg = data.data.error || data.data.error_message || 'Unknown error'
      const errorLower = errorMsg.toLowerCase()

      // Classify the error based on the message content
      if (errorLower.includes('face') || errorLower.includes('no face') || errorLower.includes('detect')) {
        throw new SkinAnalysisError(AnalysisErrorType.FACE_NOT_DETECTED, `API error: ${errorMsg}`)
      }
      if (errorLower.includes('quality') || errorLower.includes('blur') || errorLower.includes('resolution')) {
        throw new SkinAnalysisError(AnalysisErrorType.IMAGE_QUALITY, `API error: ${errorMsg}`)
      }

      throw new SkinAnalysisError(AnalysisErrorType.SERVER_ERROR, `Analysis failed: ${errorMsg}`)
    }

    // Wait 1 second before next poll
    await new Promise(resolve => setTimeout(resolve, 1000))
  }

  throw new SkinAnalysisError(AnalysisErrorType.ANALYSIS_TIMEOUT, `Timed out after ${maxAttempts} attempts`)
}

/**
 * Parse the raw score_info.json into our normalized format
 * Handles both SD (no prefix) and HD (hd_ prefix) response formats
 */
function parseScoreInfo(raw: RawScoreInfo): { skinAge: number; skinScore: number; scores: SkinAnalysisScores } {
  // Debug: Log entire raw API response
  debugLog('[parseScoreInfo] Raw API response:', raw)

  // Helper to get score from either SD or HD format
  // SD returns flat: { ui_score: 87 }
  // HD with zones: { whole: { ui_score: 87 }, forehead: {...}, ... } (wrinkle, pore, texture, acne)
  // HD flat: { ui_score: 87 } (redness, oiliness, radiance, etc.)
  const getScore = (sdKey: keyof RawScoreInfo, hdKey: keyof RawScoreInfo): number => {
    const sdValue = raw[sdKey] as { ui_score?: number; whole?: { ui_score: number } } | undefined
    const hdValue = raw[hdKey] as { ui_score?: number; whole?: { ui_score: number } } | undefined

    // Try all 4 formats: SD flat, SD nested, HD nested, HD flat
    return sdValue?.ui_score ?? sdValue?.whole?.ui_score ?? hdValue?.whole?.ui_score ?? hdValue?.ui_score ?? 50
  }

  const scores: SkinAnalysisScores = {
    // Main metrics (extract ui_score, higher = better skin condition)
    // Check SD format first (no prefix), fallback to HD format (hd_ prefix)
    wrinkles: getScore('wrinkle', 'hd_wrinkle'),
    pores: getScore('pore', 'hd_pore'),
    texture: getScore('texture', 'hd_texture'),
    acne: raw.hd_acne?.whole?.ui_score ?? 50, // HD only
    oiliness: raw.hd_oiliness?.ui_score ?? 50, // HD only
    radiance: raw.hd_radiance?.ui_score ?? 50, // HD only
    eyeBags: raw.hd_eye_bag?.ui_score ?? 50, // HD only
    spots: raw.hd_age_spot?.ui_score ?? 50, // HD only
    darkCircles: raw.hd_dark_circle?.ui_score ?? 50, // HD only
    droopyEyelidUpper: raw.hd_droopy_upper_eyelid?.ui_score ?? 50, // HD only
    droopyEyelidLower: raw.hd_droopy_lower_eyelid?.ui_score ?? 50, // HD only
    firmness: raw.hd_firmness?.ui_score ?? 50, // HD only
    moisture: raw.hd_moisture?.ui_score ?? 50, // HD only
    redness: getScore('redness', 'hd_redness'),
  }

  // Zone-specific scores (HD only - SD doesn't have zone breakdowns)
  if (raw.hd_wrinkle) {
    scores.wrinkleZones = {
      forehead: raw.hd_wrinkle.forehead?.ui_score ?? 50,
      glabellar: raw.hd_wrinkle.glabellar?.ui_score ?? 50,
      crowfeet: raw.hd_wrinkle.crowfeet?.ui_score ?? 50,
      periocular: raw.hd_wrinkle.periocular?.ui_score ?? 50,
      nasolabial: raw.hd_wrinkle.nasolabial?.ui_score ?? 50,
      marionette: raw.hd_wrinkle.marionette?.ui_score ?? 50,
    }
  }

  if (raw.hd_pore) {
    scores.poreZones = {
      forehead: raw.hd_pore.forehead?.ui_score ?? 50,
      nose: raw.hd_pore.nose?.ui_score ?? 50,
      cheek: raw.hd_pore.cheek?.ui_score ?? 50,
    }
  }

  // Debug: Log ALL parsed scores
  debugLog('[parseScoreInfo] Parsed scores (ALL):', scores)
  debugLog('[parseScoreInfo] skinAge:', raw.skin_age ?? 30, 'skinScore:', Math.round(raw.all?.score ?? 50))

  return {
    skinAge: raw.skin_age ?? 30,
    skinScore: Math.round(raw.all?.score ?? 50),
    scores,
  }
}

/**
 * Download and extract the results ZIP file
 * Downloads directly from S3 presigned URL (no proxy needed)
 */
async function downloadAndExtractResults(zipUrl: string): Promise<SkinAnalysisResult> {
  // Check online status
  if (!isOnline()) {
    throw createOfflineError()
  }

  let response: Response
  try {
    response = await fetch(zipUrl)
  } catch (fetchError) {
    const errorMsg = fetchError instanceof Error ? fetchError.message : String(fetchError)
    throw new SkinAnalysisError(AnalysisErrorType.NETWORK_ERROR, `Results download failed: ${errorMsg}`)
  }

  if (!response.ok) {
    throw new SkinAnalysisError(AnalysisErrorType.SERVER_ERROR, `Failed to download results: ${response.status}`)
  }

  let zipData: ArrayBuffer
  try {
    zipData = await response.arrayBuffer()
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : String(err)
    throw new SkinAnalysisError(AnalysisErrorType.SERVER_ERROR, `Failed to read results data: ${errorMsg}`)
  }

  let zip: JSZip
  try {
    zip = await JSZip.loadAsync(zipData)
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : String(err)
    throw new SkinAnalysisError(AnalysisErrorType.SERVER_ERROR, `Failed to parse results archive: ${errorMsg}`)
  }

  // Find and parse score_info.json
  const scoreFile = zip.file(/score_info\.json$/i)[0]
  if (!scoreFile) {
    throw new SkinAnalysisError(AnalysisErrorType.SERVER_ERROR, 'No score_info.json found in results')
  }

  const scoreJson = await scoreFile.async('string')
  const rawScores = JSON.parse(scoreJson) as RawScoreInfo
  const { skinAge, skinScore, scores } = parseScoreInfo(rawScores)

  // Extract heatmap images
  const heatmaps: Record<string, string> = {}
  const imageFiles = zip.file(/\.(png|jpg|jpeg)$/i)

  for (const imgFile of imageFiles) {
    const imgData = await imgFile.async('base64')
    const ext = imgFile.name.split('.').pop()?.toLowerCase() || 'png'
    const mimeType = ext === 'jpg' || ext === 'jpeg' ? 'image/jpeg' : 'image/png'

    // Extract metric name from filename
    // e.g., "hd_pore_output_all.png" -> "pore_all"
    // e.g., "hd_wrinkle_output_forehead.png" -> "wrinkle_forehead"
    const fileName = imgFile.name.split('/').pop() || ''
    const metricName = fileName
      .replace(/^hd_/, '')
      .replace(/_output/, '')
      .replace(/\.(png|jpg|jpeg)$/i, '')

    heatmaps[metricName] = `data:${mimeType};base64,${imgData}`
  }

  // Organize zone heatmaps into structured format for UI
  const zoneHeatmaps: ZoneHeatmaps = {}

  // Extract wrinkle zone heatmaps
  const wrinkleZoneKeys = ['forehead', 'glabellar', 'crowfeet', 'periocular', 'nasolabial', 'marionette', 'whole'] as const
  const hasWrinkleZones = wrinkleZoneKeys.some(zone => heatmaps[`wrinkle_${zone}`])
  if (hasWrinkleZones) {
    zoneHeatmaps.wrinkleZones = {}
    for (const zone of wrinkleZoneKeys) {
      const key = `wrinkle_${zone}`
      if (heatmaps[key]) {
        zoneHeatmaps.wrinkleZones[zone] = heatmaps[key]
      }
    }
  }

  // Extract pore zone heatmaps
  const poreZoneKeys = ['forehead', 'nose', 'cheek', 'whole'] as const
  const hasPoreZones = poreZoneKeys.some(zone => heatmaps[`pore_${zone}`])
  if (hasPoreZones) {
    zoneHeatmaps.poreZones = {}
    for (const zone of poreZoneKeys) {
      const key = `pore_${zone}`
      if (heatmaps[key]) {
        zoneHeatmaps.poreZones[zone] = heatmaps[key]
      }
    }
  }

  return {
    skinAge,
    skinScore,
    scores,
    heatmaps,
    zoneHeatmaps: Object.keys(zoneHeatmaps).length > 0 ? zoneHeatmaps : undefined,
  }
}

/**
 * Main function: Analyze a skin photo
 * @param file - The photo file to analyze
 * @param onProgress - Optional callback for progress updates
 * @param mode - 'teaser' for minimal analysis (saves API credits), 'full' for all 14 metrics
 */
export async function analyzeSkinPhoto(
  file: File,
  onProgress?: (stage: string, progress: number) => void,
  mode: 'teaser' | 'full' = 'teaser'
): Promise<SkinAnalysisResult> {
  // Initial online check
  if (!isOnline()) {
    throw createOfflineError()
  }

  debugLog(`[PerfectCorp] analyzeSkinPhoto called with mode: ${mode}`)

  try {
    onProgress?.('Uploading photo...', 10)
    const fileId = await uploadFile(file)

    onProgress?.('Starting analysis...', 30)
    const taskId = await startAnalysisTask(fileId, mode)

    onProgress?.('Analyzing skin...', 50)
    const resultsUrl = await pollTaskStatus(taskId)

    onProgress?.('Processing results...', 80)
    const results = await downloadAndExtractResults(resultsUrl)

    onProgress?.('Complete!', 100)
    return results
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error)
    const errorType = error instanceof SkinAnalysisError ? error.errorInfo.type : 'UNKNOWN'
    logError('PERFECTCORP_ANALYSIS', errorMsg, `mode=${mode}, errorType=${errorType}`)
    throw error
  }
}

/**
 * Convert API scores to app format (skin health scores)
 * Higher score = better skin condition (0-100 scale)
 * PerfectCorp API: higher ui_score = better condition
 * We use the API scores directly without inversion
 */
export function scoresToConcerns(scores: SkinAnalysisScores): Record<string, number> {
  return {
    wrinkles: scores.wrinkles,
    pores: scores.pores,
    texture: scores.texture,
    acne: scores.acne,
    oiliness: scores.oiliness,
    radiance: scores.radiance,
    eyeBags: scores.eyeBags,
    spots: scores.spots,
    darkCircles: scores.darkCircles,
    droopyEyelidUpper: scores.droopyEyelidUpper,
    droopyEyelidLower: scores.droopyEyelidLower,
    firmness: scores.firmness,
    moisture: scores.moisture,
    redness: scores.redness,
  }
}
